import { Component, ElementRef, ViewChild } from '@angular/core';
import { LyfisService, LyfisScorecard } from '../../../services/lyfis.service';
import { FileUtilsService } from 'projects/app-core/src/service/fileService';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { v4 as uuidv4 } from 'uuid';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { AuthService } from 'projects/app-core/src/auth/auth.service';

@Component({
    selector: 'app-scorecard-compiler',
    templateUrl: './scorecard-compiler.component.html',
    styleUrls: ['./scorecard-compiler.component.scss'],
})
export class ScorecardCompilerComponent extends AnyoComponent {
    override async ready(user?: IAnyoUser | undefined): Promise<void> {
        if (!user) return;
        this.loadDiseases();
    }
    loading = false;
    uploading = false;
    compiling = false;

    selectedDiseaseId = '';
    diseases: Array<{ id: string; name: string }> = []; // To be populated from masterdata

    uploadedScorecard: LyfisScorecard | null = null;
    compilationErrors: Array<{
        code: string;
        details: string;
        sheet?: string;
        row?: string;
        item?: string;
        currentValue?: string;
        fix?: { suggestion?: string; newValue?: string; explanation?: string };
    }> = [];

    chatMessages: Array<{
        sender: 'user' | 'agent' | 'system';
        text: string;
        timestamp: Date;
        error?: boolean;
        id?: string;
    }> = [];
    chatInput = '';
    awaitingConfirmation = false;
    awaitingWarningsConfirmation = false;

    @ViewChild('fileInput') fileInput!: ElementRef;
    @ViewChild('chatScroll') chatScroll!: ElementRef;

    constructor(
        private lyfisService: LyfisService,
        private fileService: FileUtilsService,
        private toastService: ToastService,
        private masterDataService: MasterdataService,
        auth: AuthService
    ) {
        super(auth);
    }



    loadDiseases() {
        this.masterDataService.lyfisMasterDataList().subscribe({
            next: (data) => {
                const lyfisDiseases = (data?.LyfisDiseases || []) as Array<{
                    m_id: string;
                    data?: string;
                    displayName?: string;
                    active?: boolean;
                }>;
                this.diseases = lyfisDiseases
                    .filter((d) => d.active !== false)
                    .map((d) => ({
                        id: d.m_id,
                        name: d.data || d.displayName || d.m_id
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
            error: (err: unknown) => {
                console.error('Error loading diseases:', err);
                this.toastService.showAnyoErrorToast('Failed to load diseases list');
                // Fallback to empty array
                this.diseases = [];
            }
        });
    }

    onFileSelected(event: Event) {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
            this.uploadScorecard(file);
        }
    }

    beforeUpload = (file: unknown): boolean => {
        // Prevent automatic upload, we'll handle it manually
        if (file instanceof File) {
            this.uploadScorecard(file);
        }
        return false;
    }

    getUploadStatusColor(status: string): string {
        switch (status) {
            case 'COMPLETED':
                return 'green';
            case 'PENDING':
            case 'UPLOADING':
                return 'orange';
            case 'FAILED':
                return 'red';
            default:
                return 'default';
        }
    }

    getCompileStatusColor(status: string): string {
        switch (status) {
            case 'ok':
                return 'green';
            case 'rejected':
                return 'red';
            case 'interpreting':
            case 'validating':
                return 'blue';
            default:
                return 'default';
        }
    }

    async uploadScorecard(file: File) {
        if (!this.selectedDiseaseId) {
            this.toastService.showAnyoErrorToast('Please select a disease first');
            return;
        }

        // Validate file type
        const fileExtension = file.name.toLowerCase().split('.').pop();
        if (!['xlsx', 'xls'].includes(fileExtension || '')) {
            this.toastService.showAnyoErrorToast('Invalid file type. Please upload an Excel file (.xlsx or .xls)');
            return;
        }

        // Validate file size (e.g., max 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            this.toastService.showAnyoErrorToast('File size exceeds 10MB limit');
            return;
        }

        this.uploading = true;
        try {
            // 1. Get upload URL
            const uploadResponse = await this.lyfisService
                .generateScorecardUploadUrl({
                    diseaseId: this.selectedDiseaseId,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                })
                .toPromise() as { uploadUrl: string; scorecardId: string } | undefined;

            if (!uploadResponse?.uploadUrl || !uploadResponse?.scorecardId) {
                throw new Error('Invalid response from server');
            }

            const { uploadUrl, scorecardId } = uploadResponse;

            // 2. Upload file to S3
            try {
                const contentType = file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
                await this.fileService.uploadFile(file, uploadUrl, contentType).toPromise();
            } catch (uploadError: unknown) {
                const errorMessage = uploadError instanceof Error ? uploadError.message : 'Network error';
                throw new Error(`S3 upload failed: ${errorMessage}`);
            }

            // 3. Update status
            this.uploadedScorecard = await this.lyfisService
                .updateScorecardUploadStatus(scorecardId, 'COMPLETED')
                .toPromise() as LyfisScorecard;

            if (!this.uploadedScorecard) {
                throw new Error('Failed to update upload status');
            }

            this.toastService.showSuccess('Scorecard uploaded successfully');
        } catch (error: unknown) {
            console.error('Upload error:', error);
            const errorMessage =
                (error as { error?: { description?: string } })?.error?.description ||
                (error instanceof Error ? error.message : 'Unknown error occurred');
            this.toastService.showAnyoErrorToast(`Upload failed: ${errorMessage}`);

            // Reset state on error
            this.uploadedScorecard = null;
        } finally {
            this.uploading = false;
        }
    }

    async startCompilation() {
        if (!this.uploadedScorecard) return;

        // Reset all state to allow restart at any time
        this.compiling = false;
        this.chatMessages = [];
        this.awaitingConfirmation = false;
        this.awaitingWarningsConfirmation = false;
        this.chatInput = '';

        // Set compiling flag after reset
        this.compiling = true;

        // Initial message as JSON
        const initialMessage = JSON.stringify({
            scorecardId: this.uploadedScorecard.scorecardId,
            diseaseId: this.uploadedScorecard.diseaseId,
            rulesVersionId: this.uploadedScorecard.rulesVersionId
        });

        await this.sendMessageToAgent(initialMessage, true);
    }

    async sendUserMessage() {
        if (!this.chatInput.trim()) return;

        const msg = this.chatInput;
        this.chatMessages.push({
            sender: 'user',
            text: msg,
            timestamp: new Date(),
            id: 'msg-' + uuidv4()
        });
        this.chatInput = '';
        this.scrollToBottom();

        await this.sendMessageToAgent(msg);
    }

    async sendMessageToAgent(message: string, isInitial = false) {
        if (!this.uploadedScorecard) {
            this.toastService.showAnyoErrorToast('No scorecard available');
            return;
        }

        // Validate upload status before compilation
        if (isInitial && this.uploadedScorecard.uploadStatus !== 'COMPLETED') {
            this.toastService.showAnyoErrorToast('Please wait for upload to complete');
            return;
        }

        this.compiling = true; // Show loading indicator in chat
        try {
            const result = await this.lyfisService.chatWithCompiler(
                this.uploadedScorecard.scorecardId,
                message
            ).toPromise();

            if (!result) {
                throw new Error('Empty response from compiler agent');
            }

            this.chatMessages.push({
                sender: 'agent',
                text: result.response,
                timestamp: new Date(),
                id: 'msg-' + uuidv4()
            });

            // Update scorecard state from response state
            this.awaitingConfirmation = result.state['awaitingConfirmation'] as boolean || false;
            this.awaitingWarningsConfirmation = result.state['awaitingWarningsConfirmation'] as boolean || false;

            // Refresh scorecard data to check for compiled artifacts
            const compileStatus = result.state['compileStatus'] as string;
            if (compileStatus === 'ok' || compileStatus === 'rejected') {
                await this.refreshScorecard();

                if (compileStatus === 'ok') {
                    this.toastService.showSuccess('Compilation completed successfully!');
                } else if (compileStatus === 'rejected') {
                    this.toastService.showAnyoErrorToast('Compilation was rejected. Please check errors below.');
                    await this.refreshCompilationErrorsFromLogs();
                }
            }
            // Safety net: if backend response indicates rejection but compileStatus wasn't propagated,
            // still refetch so the "Compilation Failed" table can render.
            else if (
                typeof result.response === 'string' &&
                result.response.trim().toLowerCase().startsWith('compilation rejected')
            ) {
                await this.refreshScorecard();
                this.toastService.showAnyoErrorToast('Compilation was rejected. Please check errors below.');
                await this.refreshCompilationErrorsFromLogs();
            }
        } catch (error: unknown) {
            console.error('Chat error:', error);
            const e = (error as { error?: IAnyoError })?.error;
            const errorMessage =
                e?.description ||
                (error instanceof Error ? error.message : 'Unknown error occurred');
            this.toastService.showAnyoErrorToast(`Chat error: ${errorMessage}`);

            this.chatMessages.push({
                sender: 'system',
                text: `Error: ${errorMessage}`,
                error: true,
                timestamp: new Date(),
                id: 'msg-' + uuidv4()
            });
        } finally {
            this.compiling = false;
            this.scrollToBottom();
        }
    }

    async refreshScorecard() {
        if (!this.uploadedScorecard) return;
        try {
            const refreshed = await this.lyfisService.getScorecard(this.uploadedScorecard.scorecardId).toPromise();
            if (refreshed) {
                this.uploadedScorecard = refreshed;
            }
        } catch (e: unknown) {
            console.error("Error refreshing scorecard", e);
            const errorMessage =
                (e as { error?: { description?: string } })?.error?.description ||
                (e instanceof Error ? e.message : 'Failed to refresh scorecard');
            this.toastService.showAnyoErrorToast(errorMessage);
        }
    }

    async refreshCompilationErrorsFromLogs() {
        if (!this.uploadedScorecard) return;
        try {
            const details = await this.lyfisService
                .getScorecardCompilation(this.uploadedScorecard.scorecardId)
                .toPromise();

            const logs = (details?.compileLogs || []) as Array<Record<string, unknown>>;
            const latest = logs[0] || null;
            const errors = (latest?.['errors'] || []) as Array<Record<string, unknown>>;
            this.compilationErrors = errors.map((e) => {
                const fix = e['fix'] && typeof e['fix'] === 'object' ? (e['fix'] as Record<string, unknown>) : null;
                return {
                    code: String(e['code'] || ''),
                    details: String(e['details'] || ''),
                    sheet: e['sheet'] ? String(e['sheet']) : undefined,
                    row: e['row'] ? String(e['row']) : undefined,
                    item: e['item'] ? String(e['item']) : undefined,
                    currentValue: e['currentValue'] ? String(e['currentValue']) : undefined,
                    fix: fix
                        ? {
                            suggestion: fix['suggestion'] ? String(fix['suggestion']) : undefined,
                            newValue: fix['newValue'] ? String(fix['newValue']) : undefined,
                            explanation: fix['explanation'] ? String(fix['explanation']) : undefined,
                        }
                        : undefined
                };
            });
        } catch (e: unknown) {
            console.error('Error loading compilation logs', e);
            // Don't toast; failure table will just show empty.
            this.compilationErrors = [];
        }
    }

    scrollToBottom() {
        setTimeout(() => {
            if (this.chatScroll) {
                this.chatScroll.nativeElement.scrollTop = this.chatScroll.nativeElement.scrollHeight;
            }
        }, 100);
    }

    confirmSummary(confirmed: boolean) {
        const msg = confirmed ? 'yes' : 'no';
        // Add user message visually
        this.chatMessages.push({
            sender: 'user',
            text: msg,
            timestamp: new Date(),
            id: 'msg-' + uuidv4()
        });
        this.sendMessageToAgent(msg);
        this.awaitingConfirmation = false;
    }

    confirmWarnings(confirmed: boolean) {
        const msg = confirmed ? 'yes' : 'no';
        // Add user message visually
        this.chatMessages.push({
            sender: 'user',
            text: msg,
            timestamp: new Date(),
            id: 'msg-' + uuidv4()
        });
        this.sendMessageToAgent(msg);
        this.awaitingWarningsConfirmation = false;
    }

    copyToClipboard(text: string) {
        if (!text) {
            this.toastService.showAnyoErrorToast('Nothing to copy');
            return;
        }

        navigator.clipboard.writeText(text).then(() => {
            this.toastService.showSuccess('Copied to clipboard');
        }).catch((err) => {
            console.error('Failed to copy:', err);
            this.toastService.showAnyoErrorToast('Failed to copy to clipboard');
        });
    }

    copyCompiledConfig() {
        if (!this.uploadedScorecard?.compiledConfig) {
            this.toastService.showAnyoErrorToast('Nothing to copy');
            return;
        }
        const text = JSON.stringify(this.uploadedScorecard.compiledConfig, null, 2);
        this.copyToClipboard(text);
    }

    copyCompiledEngineCode() {
        const text = this.uploadedScorecard?.compiledEngineCode || '';
        this.copyToClipboard(text);
    }

    formatErrors(
        errors: Array<{
            code: string;
            details: string;
            sheet?: string;
            row?: string;
            item?: string;
            currentValue?: string;
            fix?: { suggestion?: string; newValue?: string; explanation?: string };
        }>
    ): Array<{
        id: number;
        type: string;
        location: string;
        current: string;
        fix: string;
        description: string;
    }> {
        if (!Array.isArray(errors) || errors.length === 0) {
            return [];
        }

        return errors.map((e, idx) => {
            const sheet = e.sheet || 'Unknown';
            const item = e.item || 'Unknown';
            const row = e.row ? ` (${e.row})` : '';
            const loc = `${sheet}: ${item}${row}`;

            return {
                id: idx + 1,
                type: e.code,
                location: loc,
                current: e.currentValue || 'N/A',
                fix: e.fix?.newValue || 'N/A',
                description: e.fix?.explanation || e.details
            };
        });
    }
}

